// Библиотеки (README §8): шапка, поиск, вкладки «все / скилы / субагенты / MCP»
// со счётчиками, сетка карточек auto-fill minmax(280px,1fr). «установить» /
// «на сервере ✓». Из карточки Конструктора — плашка «выбор для элемента «…»»
// и кнопка «в схему». Боковое меню: «каталог» по типам и «установлено».
// Данные — GET /api/library, POST /api/library/install.

import { useEffect } from 'react'
import { TypeIcon } from '../../ui/Icon'
import type { ElementType } from '../../ui/Icon'
import { SideMenu } from '../../ui/SideMenu'
import type { SectionModule } from '../types'
import { cancelPick, fillPick, usePick } from './pick'
import { useLibrary, visible } from './store'
import type { Tab } from './store'
import type { LibraryItem, LibType } from './types'
import './library.css'

const TYPE_ICON: Record<LibType, ElementType> = { skill: 'skill', agent: 'sub', mcp: 'mcp' }
const TYPE_TEXT: Record<LibType, string> = { skill: 'скил', agent: 'субагент', mcp: 'MCP' }
const TABS: { id: Tab; label: string }[] = [
  { id: 'all', label: 'все' },
  { id: 'skill', label: 'скилы' },
  { id: 'agent', label: 'субагенты' },
  { id: 'mcp', label: 'MCP' },
]

function useLoaded() {
  const loaded = useLibrary((s) => s.loaded)
  useEffect(() => {
    if (!loaded) void useLibrary.getState().load()
  }, [loaded])
}

/** Пришли из карточки Конструктора — сразу нужная вкладка и поиск. */
function usePickPreset() {
  const pick = usePick()
  useEffect(() => {
    if (!pick) return
    const lib = useLibrary.getState()
    if (pick.tab) lib.setTab(pick.tab)
    if (pick.q) lib.setQ(pick.q)
  }, [pick?.node, pick?.tab, pick?.q]) // eslint-disable-line react-hooks/exhaustive-deps
}

function Sidebar() {
  useLoaded()
  const counts = useLibrary((s) => s.counts)
  const tab = useLibrary((s) => s.tab)
  const installedOnly = useLibrary((s) => s.installedOnly)
  const setTab = useLibrary((s) => s.setTab)
  return (
    <SideMenu
      screen="library"
      sections={[{
        id: 'lib',
        title: 'Библиотеки',
        items: [
          {
            id: 'catalog',
            label: 'каталог',
            children: TABS.map((t) => ({
              id: t.id,
              label: t.label,
              meta: counts ? String(counts[t.id]) : undefined,
              active: !installedOnly && tab === t.id,
              onSelect: () => setTab(t.id),
            })),
          },
          {
            id: 'installed',
            label: 'установлено',
            meta: counts ? String(counts.installed) : undefined,
            active: installedOnly,
            onSelect: () => setTab('all', !installedOnly),
          },
        ],
      }]}
    />
  )
}

function Card({ item, picking }: { item: LibraryItem; picking: boolean }) {
  const busy = useLibrary((s) => !!s.busy[item.id])
  const error = useLibrary((s) => s.errors[item.id])
  const install = useLibrary((s) => s.install)
  const meta = [item.source, item.author, item.installs ? `${item.installs} установок` : null].filter(Boolean).join(' · ')
  let button
  if (picking) {
    button = <button className="abx-lib__btn is-ok" onClick={() => void fillPick(item)}>в схему</button>
  } else if (item.installed) {
    button = <span className="abx-lib__done" title={item.installPath ?? undefined}>на сервере ✓</span>
  } else if (item.catalog) {
    button = (
      <button className="abx-lib__btn" disabled={busy} onClick={() => void install(item.id)}>
        {busy ? 'ставлю…' : 'установить'}
      </button>
    )
  } else {
    button = null
  }
  return (
    <div className="abx-lib__card">
      <div className="abx-lib__head">
        <TypeIcon type={TYPE_ICON[item.type]} width={20} />
        <span className="abx-lib__name" title={item.name}>{item.title || item.name}</span>
        <span className="abx-lib__type">{TYPE_TEXT[item.type]}</span>
      </div>
      <div className="abx-lib__desc">{item.description}</div>
      <div className="abx-lib__foot">
        {item.homepage ? (
          <a className="abx-lib__meta" href={item.homepage} target="_blank" rel="noreferrer noopener" title={meta}>{meta}</a>
        ) : (
          <span className="abx-lib__meta" title={meta}>{meta}</span>
        )}
        {button}
      </div>
      {error ? (
        <div className="abx-lib__err">
          {error.text}
          {error.conflict ? (
            <button className="abx-lib__link" onClick={() => void install(item.id, true)}>заменить</button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function View() {
  useLoaded()
  usePickPreset()
  const { items, counts, loaded, error, tab, installedOnly, q } = useLibrary()
  const setTab = useLibrary((s) => s.setTab)
  const setQ = useLibrary((s) => s.setQ)
  const pick = usePick()
  const list = visible(items, tab, installedOnly, q)
  return (
    <div className="abx-lib">
      <div className="abx-lib__page">
        {pick ? (
          <div className="abx-lib__pick">
            <span>выбор для элемента «{pick.title || pick.node}»</span>
            <button className="abx-lib__link" onClick={cancelPick} title="не выбирать">×</button>
          </div>
        ) : null}
        <div className="abx-lib__top">
          <h1 className="abx-lib__h1">Библиотеки</h1>
          <span className="abx-lib__sub">скилы, субагенты и MCP из открытых источников</span>
        </div>
        <div className="abx-lib__bar">
          <input
            className="abx-lib__search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="поиск: pdf, github, ревью…"
            aria-label="поиск по библиотекам"
          />
          <div className="abx-lib__tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                className={`abx-lib__tab${tab === t.id ? ' is-on' : ''}`}
                onClick={() => setTab(t.id, installedOnly)}
              >
                {t.label} <span className="abx-lib__count">{counts ? counts[t.id] : ''}</span>
              </button>
            ))}
          </div>
        </div>
        {error ? <p className="abx-lib__note is-warn">каталог не загрузился: {error}</p> : null}
        {loaded && !error && !list.length ? <p className="abx-lib__note">ничего не нашлось</p> : null}
        <div className="abx-lib__grid">
          {list.map((item) => <Card key={item.id} item={item} picking={!!pick} />)}
        </div>
      </div>
    </div>
  )
}

function useRail() {
  const n = useLibrary((s) => s.counts?.all)
  return { count: n ? String(n) : undefined, tip: 'каталог' }
}

const library: SectionModule = { Sidebar, View, useRail }
export default library
